import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Badge, Spinner, Alert, Form,
  Row, Col, ProgressBar, Button, Table,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight, faSync, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { getPurchaseOrders, getPurchaseOrderDetail, exportPurchaseOrders } from '../services/api';

const PAGE_LIMIT = 50;

// ── Column definitions ─────────────────────────────────────────────────────────
// widths must add to 100% and match exactly between header and body tables
const COLS = [
  { key: 'expand',    label: '',            width: '3%',  align: 'center'  },
  { key: 'poNumber',  label: 'PO Number',   width: '12%', align: 'left'    },
  { key: 'vendor',    label: 'Vendor',      width: '18%', align: 'left'    },
  { key: 'expected',  label: 'Expected',    width: '11%', align: 'left'    },
  { key: 'status',    label: 'Status',      width: '9%',  align: 'left'    },
  { key: 'pct',       label: 'Fulfillment', width: '16%', align: 'left'    },
  { key: 'warehouse', label: 'Warehouse',   width: '15%', align: 'left'    },
  { key: 'ordered',   label: 'Ordered',     width: '8%',  align: 'right'   },
  { key: 'received',  label: 'Received',    width: '8%',  align: 'right'   },
];

const TH_STYLE = {
  background: '#212529',
  color: '#fff',
  fontWeight: 600,
  fontSize: '0.8rem',
  whiteSpace: 'nowrap',
  padding: '8px 10px',
  border: 'none',
  borderBottom: '2px solid #495057',
};

const TD_STYLE = {
  padding: '7px 10px',
  verticalAlign: 'middle',
  fontSize: '0.875rem',
  borderBottom: '1px solid #dee2e6',
};

const TABLE_STYLE = {
  tableLayout: 'fixed',
  width: '100%',
  borderCollapse: 'collapse',
  margin: 0,
};

// ── Helpers ────────────────────────────────────────────────────────────────────
const STATUS_META = {
  OPEN:      { label: 'Open',      bg: 'primary'   },
  PARTIAL:   { label: 'Partial',   bg: 'warning'   },
  RECEIVED:  { label: 'Received',  bg: 'success'   },
  CANCELLED: { label: 'Cancelled', bg: 'secondary' },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status?.toUpperCase()] || { label: status, bg: 'secondary' };
  return <Badge bg={meta.bg}>{meta.label}</Badge>;
}

function fmtDate(val) {
  if (!val) return '—';
  return new Date(val).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function fmtQty(val) {
  if (val == null) return '—';
  return Number(val).toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function PctBar({ pct }) {
  const n = Number(pct) || 0;
  const variant = n >= 100 ? 'success' : n > 0 ? 'warning' : 'secondary';
  return (
    <div>
      <ProgressBar now={n} variant={variant} style={{ height: 7, borderRadius: 4 }} />
      <small className="text-muted" style={{ fontSize: '0.72rem' }}>{n.toFixed(1)}%</small>
    </div>
  );
}

// ── Colgroup shared between both tables for pixel-perfect column alignment ─────
function ColGroup() {
  return (
    <colgroup>
      {COLS.map(c => <col key={c.key} style={{ width: c.width }} />)}
    </colgroup>
  );
}

// ── Detail panel ───────────────────────────────────────────────────────────────
function PODetail({ data }) {
  const hasItems = data.items && data.items.length > 0;
  const hasNotes = !!data.notes;
  if (!hasItems && !hasNotes) return null;

  return (
    <div style={{ background: '#f8f9fa', borderTop: '2px solid #dee2e6', padding: '10px 16px' }}>
      {hasItems && (
        <Table size="sm" bordered className="mb-0 bg-white" style={{ fontSize: '0.8rem' }}>
          <thead>
            <tr>
              {['SKU', 'Description', 'Ordered', 'Received', '% Complete'].map((h, i) => (
                <th key={h} className={i >= 2 ? 'text-end' : ''}
                  style={{ background: '#e9ecef', padding: '5px 8px', whiteSpace: 'nowrap' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.items.map(item => (
              <tr key={item.idPOItem}>
                <td style={{ padding: '4px 8px' }}><code>{item.sku}</code></td>
                <td style={{ padding: '4px 8px' }}>{item.description || '—'}</td>
                <td className="text-end" style={{ padding: '4px 8px' }}>{fmtQty(item.orderedQty)}</td>
                <td className="text-end" style={{ padding: '4px 8px' }}>{fmtQty(item.receivedQty)}</td>
                <td style={{ padding: '4px 8px', minWidth: 120 }}><PctBar pct={item.pctReceived} /></td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {hasNotes && (
        <p className="small text-muted mb-0" style={{ marginTop: hasItems ? 8 : 0, whiteSpace: 'pre-wrap' }}>
          <strong>Notes:</strong> {data.notes}
        </p>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function PurchaseOrders() {
  const [pos, setPos]               = useState([]);
  const [loading, setLoading]       = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exporting, setExporting]   = useState(false);
  const [error, setError]           = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [page, setPage]             = useState(1);
  const [hasMore, setHasMore]       = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const detailCache = useRef({});
  const sentinelRef = useRef(null);
  const [, forceUpdate] = useState(0);

  const [statusFilter, setStatusFilter] = useState('OPEN');
  const [dateFrom, setDateFrom]         = useState('');
  const [dateTo, setDateTo]             = useState('');
  const [search, setSearch]             = useState('');

  const buildParams = useCallback((pg) => {
    const params = { page: pg, limit: PAGE_LIMIT };
    if (statusFilter) params.status   = statusFilter;
    if (dateFrom)     params.dateFrom = dateFrom;
    if (dateTo)       params.dateTo   = dateTo;
    if (search)       params.search   = search;
    return params;
  }, [statusFilter, dateFrom, dateTo, search]);

  const fetchPOs = useCallback(async (pg = 1, append = false) => {
    if (pg === 1) setLoading(true); else setLoadingMore(true);
    setError(null);
    try {
      const result = await getPurchaseOrders(buildParams(pg));
      const items  = result?.items ?? [];
      const pag    = result?.pagination ?? {};
      setPos(prev  => append ? [...prev, ...items] : items);
      setHasMore(pag.hasMore ?? false);
      setTotalCount(pag.total ?? items.length);
      setPage(pg);
    } catch {
      setError('Failed to load purchase orders. Please try again.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [buildParams]);

  // Reset to page 1 when filters change
  useEffect(() => { fetchPOs(1, false); }, [fetchPOs]);

  // Infinite scroll sentinel
  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && hasMore && !loadingMore) fetchPOs(page + 1, true); },
      { threshold: 0.1 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, page, fetchPOs]);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const params = {};
      if (statusFilter) params.status   = statusFilter;
      if (dateFrom)     params.dateFrom = dateFrom;
      if (dateTo)       params.dateTo   = dateTo;
      if (search)       params.search   = search;
      const response = await exportPurchaseOrders(params);
      const url  = URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href  = url;
      link.setAttribute('download', 'purchase_orders_export.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  }, [statusFilter, dateFrom, dateTo, search]);

  const toggleExpand = useCallback((id) => {
    setExpandedId(prev => {
      const next = prev === id ? null : id;
      if (next !== null && !detailCache.current[next]) {
        detailCache.current[next] = { loading: true, data: null, error: null };
        getPurchaseOrderDetail(next)
          .then(d  => { detailCache.current[next] = { loading: false, data: d,    error: null };         forceUpdate(n => n + 1); })
          .catch(() => { detailCache.current[next] = { loading: false, data: null, error: 'Failed to load.' }; forceUpdate(n => n + 1); });
      }
      return next;
    });
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

      {/* ── Filter bar ── */}
      <div style={{ flexShrink: 0, padding: '12px 0 8px', borderBottom: '1px solid #dee2e6' }}>
        <Row className="g-2 align-items-end">
          <Col xs={12} sm={4} md={3}>
            <Form.Label className="small fw-semibold mb-1">Status</Form.Label>
            <Form.Select size="sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="">All Statuses</option>
              <option value="OPEN">Open</option>
              <option value="PARTIAL">Partial</option>
              <option value="RECEIVED">Received</option>
              <option value="CANCELLED">Cancelled</option>
            </Form.Select>
          </Col>
          <Col xs={12} sm={4} md={3}>
            <Form.Label className="small fw-semibold mb-1">Search</Form.Label>
            <Form.Control
              type="text" size="sm"
              placeholder="PO # or vendor…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </Col>
          <Col xs={6} sm={3} md={2}>
            <Form.Label className="small fw-semibold mb-1">From</Form.Label>
            <Form.Control type="date" size="sm" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </Col>
          <Col xs={6} sm={3} md={2}>
            <Form.Label className="small fw-semibold mb-1">To</Form.Label>
            <Form.Control type="date" size="sm" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </Col>
          <Col xs="auto">
            <Button size="sm" variant="outline-secondary" onClick={() => fetchPOs(1, false)} title="Refresh">
              <FontAwesomeIcon icon={faSync} />
            </Button>
          </Col>
          <Col xs="auto">
            <Button size="sm" variant="outline-success" onClick={handleExport} disabled={exporting} title="Export to Excel">
              <FontAwesomeIcon icon={faFileExcel} className="me-1" />
              {exporting ? 'Exporting…' : 'Export'}
            </Button>
          </Col>
          <Col xs="auto" className="ms-auto">
            <span className="text-muted small">
              {pos.length}{hasMore ? `+ of ${totalCount}` : ''} PO{totalCount !== 1 ? 's' : ''}
            </span>
          </Col>
        </Row>
        {error && <Alert variant="danger" className="mt-2 mb-0 py-2">{error}</Alert>}
      </div>

      {/* ── Fixed header table ── */}
      {!loading && pos.length > 0 && (
        <div style={{ flexShrink: 0, overflowX: 'auto', background: '#212529' }}>
          <table style={TABLE_STYLE}>
            <ColGroup />
            <thead>
              <tr>
                {COLS.map(c => (
                  <th key={c.key} style={{ ...TH_STYLE, textAlign: c.align }}>{c.label}</th>
                ))}
              </tr>
            </thead>
          </table>
        </div>
      )}

      {/* ── Scrollable body ── */}
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'auto' }}>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted">Loading purchase orders…</p>
          </div>
        ) : pos.length === 0 ? (
          <Alert variant="info" className="mt-3">No purchase orders found for the selected filters.</Alert>
        ) : (
          <table style={{ ...TABLE_STYLE, background: '#fff' }}>
            <ColGroup />
            <tbody>
              {pos.map(po => {
                const isOpen  = expandedId === po.idPO;
                const cached  = detailCache.current[po.idPO];
                const rowBg   = isOpen ? '#e8f0fe' : undefined;
                return (
                  <React.Fragment key={po.idPO}>
                    <tr
                      onClick={() => toggleExpand(po.idPO)}
                      style={{ cursor: 'pointer', background: rowBg }}
                      onMouseEnter={e => { if (!isOpen) e.currentTarget.style.background = '#f5f5f5'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = rowBg || ''; }}
                    >
                      <td style={{ ...TD_STYLE, textAlign: 'center', color: '#6c757d' }}>
                        <FontAwesomeIcon icon={isOpen ? faChevronDown : faChevronRight} size="xs" />
                      </td>
                      <td style={{ ...TD_STYLE, fontWeight: 600 }}>{po.poNumber}</td>
                      <td style={TD_STYLE}>{po.vendorName || '—'}</td>
                      <td style={TD_STYLE}>{fmtDate(po.expectedDate)}</td>
                      <td style={TD_STYLE}><StatusBadge status={po.status} /></td>
                      <td style={TD_STYLE}><PctBar pct={po.pctComplete} /></td>
                      <td style={TD_STYLE}>{po.warehouseName || '—'}</td>
                      <td style={{ ...TD_STYLE, textAlign: 'right' }}>{fmtQty(po.totalOrdered)}</td>
                      <td style={{ ...TD_STYLE, textAlign: 'right' }}>{fmtQty(po.totalReceived)}</td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={COLS.length} style={{ padding: 0, borderBottom: '1px solid #dee2e6' }}>
                          {!cached || cached.loading ? (
                            <div className="text-center py-2">
                              <Spinner animation="border" size="sm" />
                            </div>
                          ) : cached.error ? (
                            <Alert variant="danger" className="m-2 py-1 mb-1">{cached.error}</Alert>
                          ) : (
                            <PODetail data={cached.data} />
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
        {/* Infinite scroll sentinel */}
        <div ref={sentinelRef} style={{ height: 1 }} />
        {loadingMore && (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" variant="primary" />
            <span className="ms-2 text-muted small">Loading more…</span>
          </div>
        )}
      </div>
    </div>
  );
}
